import { computed, unref } from "vue";

/**
 * Общая логика навигации по вкладкам документа и тихого автосохранения при
 * смене вкладки. Используется на страницах нотариального действия,
 * наследственного дела и исполнительной надписи.
 *
 * Каждая страница оставляет свой changeTab (со своей логикой router/pendingFields),
 * но в его начале вызывает maybeAutosaveBeforeLeave(tab); если вернулось false —
 * переход нужно отменить.
 *
 * @param {object} o
 * @param {import('vue').Ref|import('vue').ComputedRef} o.tabs — объект вкладок
 * @param {import('vue').Ref} o.activeTab — текущая вкладка
 * @param {import('vue').ComputedRef<boolean>} o.isFormDirty
 * @param {() => string} o.serializeContent — снимок контента
 * @param {import('vue').Ref} o.savedContentSnapshot
 * @param {() => boolean} o.canAutosave — напр. !view && status===PROJECT
 * @param {() => object} o.buildPayload — собрать payload для сохранения
 * @param {(payload: object) => Promise<any>} o.update — сохранить (тихо)
 * @param {(tab: string) => Promise<void>} o.changeTab — переход (для prev/next)
 * @param {() => boolean} [o.onBlockedNext] — вернуть true, если «Далее» заблокирован и обработан
 * @param {() => void} [o.onAutosaveError] — колбэк при неудаче сохранения перед sign
 */
export function useTabFlow({
  tabs,
  activeTab,
  isFormDirty,
  serializeContent,
  savedContentSnapshot,
  canAutosave,
  buildPayload,
  update,
  changeTab,
  onBlockedNext,
  onAutosaveError,
}) {
  const tabKeysOrder = computed(() => Object.keys(unref(tabs) || {}));
  const currentTabIndex = computed(() =>
    tabKeysOrder.value.indexOf(unref(activeTab))
  );
  const totalTabs = computed(() => tabKeysOrder.value.length);
  const hasPrevTab = computed(() => currentTabIndex.value > 0);
  const hasNextTab = computed(
    () => currentTabIndex.value < totalTabs.value - 1
  );

  let autosavePromise = null;

  async function autosaveForm() {
    if (autosavePromise) return autosavePromise;
    autosavePromise = (async () => {
      try {
        const snapshot = serializeContent();
        const payload = buildPayload();
        const data = await update(payload);
        const ok = !!(data?.success || data?.ok);
        if (ok) savedContentSnapshot.value = snapshot;
        return ok;
      } catch {
        return false;
      } finally {
        autosavePromise = null;
      }
    })();
    return autosavePromise;
  }

  async function maybeAutosaveBeforeLeave(tab) {
    if (!canAutosave() || !isFormDirty.value) return true;
    if (tab === "sign") {
      let ok = await autosaveForm();
      if (ok && isFormDirty.value) ok = await autosaveForm();
      if (!ok) {
        onAutosaveError?.();
        return false;
      }
      return true;
    }
    autosaveForm();
    return true;
  }

  async function goToPrevTab() {
    if (hasPrevTab.value) {
      await changeTab(tabKeysOrder.value[currentTabIndex.value - 1]);
    }
  }

  async function goToNextTab() {
    if (hasNextTab.value) {
      await changeTab(tabKeysOrder.value[currentTabIndex.value + 1]);
    }
  }

  function tryGoNext() {
    if (onBlockedNext && onBlockedNext()) return;
    goToNextTab();
  }

  return {
    tabKeysOrder,
    currentTabIndex,
    totalTabs,
    hasPrevTab,
    hasNextTab,
    autosaveForm,
    maybeAutosaveBeforeLeave,
    goToPrevTab,
    goToNextTab,
    tryGoNext,
  };
}
